import { useState, useEffect, type SyntheticEvent } from 'react';
import key from 'weak-key';
import { Row, Col, AccordionItem, Container } from '@geberit/gdds';

// styles
import styles from './online-catalog-assets.module.scss';

// types
import type { AccordionViewProps } from './online-catalog-assets.types';

// components
import OkvTilesView from './okv-tiles-view';

// utils
import { accordionClick } from 'components/ContentElementsGdds/Accordion/tracking-actions';
import { useHash } from 'utils/hooks/useHash';
import { useTracking } from 'utils/hooks/useTracking';
import { decodingContent } from 'utils/decodingContent';
import { isEmpty } from 'utils/is-empty';
import { classNameBuilder } from '../../../utils/classNameBuilder';

const AccordionView = ({ items, carousel, backgroundColor }: AccordionViewProps) => {
  const [openedItems, setOpenedItems] = useState<unknown[]>([]);
  const { matchIndex } = useHash(items);
  const track = useTracking();

  useEffect(() => {
    const openIndex = matchIndex !== -1 && matchIndex;

    if (openIndex || openIndex === 0) {
      setOpenedItems([openIndex]);
    }
  }, [matchIndex]);

  const onClick = (e: SyntheticEvent<HTMLDivElement, Event> | undefined, index: number) => {
    track.trackEvent(accordionClick('Clicks', e?.currentTarget.innerText));

    if (openedItems.includes(index)) setOpenedItems(openedItems.filter((item) => item !== index));
    else setOpenedItems([...openedItems, index]);
  };

  return (
    <Container>
      <Row>
        <Col>
          {items.map((accordionItem, index) => {
            let open = false;

            if (openedItems?.length === items.length || openedItems?.includes(index)) {
              open = true;
            }

            const content = !isEmpty(accordionItem.items) && (
              <div className="items">
                <OkvTilesView
                  items={accordionItem.items}
                  carousel={carousel}
                  isAccordion={true}
                  backgroundColor={backgroundColor}
                  size={[4, 8, 12]}
                />
              </div>
            );

            return (
              <div
                key={key(accordionItem)}
                className={classNameBuilder(styles.accordionWrapper, styles[backgroundColor])}
              >
                {content && (
                  <AccordionItem
                    iconAlignment="right"
                    title={decodingContent(accordionItem.title)}
                    opened={open}
                    onTitleClick={(e) => onClick(e, index)}
                    titlePreviewId="#st_accordionLabel"
                    contentPreviewId="#st_content"
                    fontSize="p1"
                    tag="h3"
                    titleBold
                  >
                    {content}
                  </AccordionItem>
                )}
              </div>
            );
          })}
        </Col>
      </Row>
    </Container>
  );
};

export default AccordionView;
